<template>
  <component :is="teleportToBody ? 'Teleport' : 'div'" v-bind="teleportToBody ? { to: 'body' } : {}">
    <div class="c-json-editor fixed inset-0 bg-base z-10000 flex flex-col">
      <div class="json-editor-container flex-1 overflow-auto" />
      <div class="flex justify-end p-2 bg-primary6">
        <CButton class="mr-2" @click="closeEditor">{{ t('canvasUI.common.close') }}</CButton>
        <CButton type="primary" @click="updateObject">{{ t('canvasUI.common.update') }}</CButton>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

import { loadStyle } from '@lib/util/dom'
import CButton from '@lib/components/CButton/CButton.vue'
import useThemeVars from '@lib/composables/useThemeVars'

const props = withDefaults(
  defineProps<{
    object?: Record<string, any>
    teleportToBody?: boolean
  }>(),
  {
    teleportToBody: true,
  },
)

const emit = defineEmits<{
  close: []
  update: [o: Record<string, any>]
}>()

const themeVars = useThemeVars()
const { t } = useI18n()

const SCRIPT_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jsoneditor/10.0.3/jsoneditor.min.js'
const STYLE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/jsoneditor/10.0.3/jsoneditor.min.css'

let jsonEditor: any = null

function restoreAMDLoader(originalDefine: any, originalRequire: any) {
  if (originalDefine) (window as any).define = originalDefine
  if (originalRequire) (window as any).require = originalRequire
}

async function loadJSONEditorScript() {
  const existingScript = document.querySelector(`script[src="${SCRIPT_URL}"]`)
  if (existingScript && !window.JSONEditor) {
    existingScript.remove()
  }

  const originalDefine = (window as any).define
  const originalRequire = (window as any).require
  ;(window as any).define = undefined
  ;(window as any).require = undefined

  if (!window.JSONEditor) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SCRIPT_URL
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load JSONEditor script'))
      document.head.appendChild(script)
    }).catch((error) => {
      console.error('Error loading JSONEditor script:', error)
    })
  }

  restoreAMDLoader(originalDefine, originalRequire)
}

function initializeEditor() {
  if (jsonEditor) jsonEditor.destroy()

  const container = document.querySelector('.json-editor-container')

  jsonEditor = new window.JSONEditor(container, {
    mode: 'code',
    onChange: () => {
      const editorElement = container?.querySelector<HTMLDivElement>('.jsoneditor')
      if (editorElement) {
        editorElement.style.backgroundColor = '#29335C'
      }
    },
  })

  jsonEditor.set(props.object)
  document.querySelector('.jsoneditor-poweredBy')?.remove()
}

onMounted(async () => {
  loadStyle(STYLE_URL)
  await loadJSONEditorScript()
  await nextTick()

  if (!window.JSONEditor) {
    console.error('Failed to load JSONEditor')
    return
  }

  initializeEditor()
})

onUnmounted(() => {
  jsonEditor?.destroy()
  jsonEditor = null
})

function closeEditor() {
  jsonEditor?.destroy()
  jsonEditor = null
  emit('close')
}

function updateObject() {
  try {
    const updatedObject = jsonEditor.get()
    emit('update', updatedObject)
    closeEditor()
  } catch (error) {
    alert('The JSON is invalid 🤦. Please correct it and try again.')
  }
}
</script>

<style lang="scss" scoped>
.c-json-editor {
  font-family: Roboto, sans-serif; // stop any themes from altering our json editor
}

:deep(.jsoneditor) {
  border: 1px solid v-bind('themeVars.primary6');
}

:deep(.jsoneditor-menu) {
  background-color: v-bind('themeVars.primary');
  border-bottom: 1px solid v-bind('themeVars.primary');
}
</style>
